import React, { useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Label } from '../ui/label';
import { Plus, User } from 'lucide-react';

const el = React.createElement;

// Reusable component for selecting a person from the centralized people list
// or adding a new person on the fly
export function PersonSelect({ 
    value, 
    onChange, 
    people = [], 
    onAddPerson,
    placeholder = "Select a person",
    includeSpouse = false,
    spouse = null,
    includeChildren = false,
    children = [],
    showRelationship = true,
    className = ""
}) {
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [newPerson, setNewPerson] = useState({ name: '', relationship: '', phone: '', email: '' });

    // Build options list from all available people
    const allPeople = [];
    
    // Add spouse if included and has a name
    if (includeSpouse && spouse?.name) {
        allPeople.push({ 
            id: 'spouse', 
            name: spouse.name, 
            relationship: 'Spouse',
            phone: spouse.phone || '',
            source: 'spouse'
        });
    }
    
    // Add children if included
    if (includeChildren && children?.length > 0) {
        children.forEach((child, idx) => {
            if (child.name) {
                allPeople.push({
                    id: `child-${idx}`,
                    name: child.name,
                    relationship: 'Child',
                    phone: child.phone || '',
                    source: 'children'
                });
            }
        });
    }
    
    // Add people from centralized list
    if (people?.length > 0) {
        people.forEach((person, idx) => {
            if (person.name) {
                allPeople.push({
                    id: `person-${idx}`,
                    name: person.name,
                    relationship: person.relationship || '',
                    phone: person.phone || '',
                    source: 'people'
                });
            }
        });
    }

    const handleAddPerson = () => {
        if (newPerson.name.trim()) {
            if (onAddPerson) {
                onAddPerson(newPerson);
            }
            onChange(newPerson.name);
            setNewPerson({ name: '', relationship: '', phone: '', email: '' });
            setShowAddDialog(false);
        }
    };

    return el('div', { className: `flex gap-2 ${className}` },
        el(Select, { 
            value: value || '', 
            onValueChange: (val) => {
                if (val === '__add_new__') {
                    setShowAddDialog(true);
                } else {
                    onChange(val);
                }
            }
        },
            el(SelectTrigger, { className: 'rounded-none flex-1' },
                el(SelectValue, { placeholder })
            ),
            el(SelectContent, null,
                allPeople.length === 0 
                    ? el(SelectItem, { value: '__no_people__', disabled: true }, 'No people added yet')
                    : allPeople.map((person) => 
                        el(SelectItem, { 
                            key: person.id, 
                            value: person.name 
                        }, 
                            showRelationship && person.relationship 
                                ? `${person.name} (${person.relationship})`
                                : person.name
                        )
                    ),
                el(SelectItem, { value: '__add_new__', className: 'text-amber-600 font-medium' },
                    el('span', { className: 'flex items-center gap-2' },
                        el(Plus, { className: 'h-4 w-4' }),
                        'Add New Person'
                    )
                )
            )
        ),
        
        // Add New Person Dialog
        el(Dialog, { open: showAddDialog, onOpenChange: setShowAddDialog },
            el(DialogContent, { className: 'rounded-none' },
                el(DialogHeader, null,
                    el(DialogTitle, { className: 'font-serif' }, 'Add New Person')
                ),
                el('div', { className: 'space-y-4 pt-4' },
                    el('div', { className: 'space-y-2' },
                        el(Label, null, 'Full Name *'),
                        el(Input, { 
                            value: newPerson.name,
                            onChange: (e) => setNewPerson({ ...newPerson, name: e.target.value }),
                            placeholder: 'Enter full name',
                            className: 'rounded-none'
                        })
                    ),
                    el('div', { className: 'space-y-2' },
                        el(Label, null, 'Relationship'),
                        el(Select, { 
                            value: newPerson.relationship,
                            onValueChange: (val) => setNewPerson({ ...newPerson, relationship: val })
                        },
                            el(SelectTrigger, { className: 'rounded-none' },
                                el(SelectValue, { placeholder: 'Select relationship' })
                            ),
                            el(SelectContent, null,
                                ['Spouse', 'Child', 'Parent', 'Sibling', 'Friend', 'Attorney', 'Accountant', 'Other'].map(rel =>
                                    el(SelectItem, { key: rel, value: rel.toLowerCase() }, rel)
                                )
                            )
                        )
                    ),
                    el('div', { className: 'space-y-2' },
                        el(Label, null, 'Phone'),
                        el(Input, { 
                            value: newPerson.phone,
                            onChange: (e) => setNewPerson({ ...newPerson, phone: e.target.value }),
                            placeholder: '(555) 555-5555',
                            className: 'rounded-none'
                        })
                    ),
                    el('div', { className: 'flex justify-end gap-2 pt-4' },
                        el(Button, { 
                            variant: 'outline', 
                            onClick: () => setShowAddDialog(false),
                            className: 'rounded-none'
                        }, 'Cancel'),
                        el(Button, { 
                            onClick: handleAddPerson,
                            disabled: !newPerson.name.trim(),
                            className: 'rounded-none'
                        }, 'Add Person')
                    )
                )
            )
        )
    );
}

// Simple inline input that shows suggestions from people list
// Also auto-adds new names to the people list
export function PersonInput({
    value,
    onChange,
    onAddPerson,  // Callback to add person to centralized list
    people = [],
    spouse = null,
    children = [],
    placeholder = "Enter name",
    relationship = "",  // Default relationship for auto-added people
    className = ""
}) {
    const [showSuggestions, setShowSuggestions] = useState(false);
    
    // Build suggestions list
    const suggestions = [];
    if (spouse?.name) {
        suggestions.push({ name: spouse.name, relationship: 'Spouse' });
    }
    if (children?.length > 0) {
        children.forEach(child => {
            if (child.name) {
                suggestions.push({ name: child.name, relationship: 'Child' });
            }
        });
    }
    if (people?.length > 0) {
        people.forEach(person => {
            if (person.name) {
                suggestions.push({ name: person.name, relationship: person.relationship || '' });
            }
        });
    }
    
    // Filter suggestions based on current input
    const filteredSuggestions = value 
        ? suggestions.filter(s => s.name.toLowerCase().includes(value.toLowerCase()))
        : suggestions;

    // Check if the name is new (not already in suggestions)
    const isNewName = (name) => {
        if (!name || name.trim().length < 2) return false;
        return !suggestions.some(s => s.name.toLowerCase() === name.toLowerCase().trim());
    };

    // Auto-add person when input loses focus
    const handleBlur = () => {
        setTimeout(() => {
            setShowSuggestions(false);
            // If it's a new name and we have a callback, add to people list
            if (value && isNewName(value) && onAddPerson) {
                onAddPerson({ name: value.trim(), relationship: relationship });
            }
        }, 200);
    };

    return el('div', { className: `relative ${className}` },
        el(Input, {
            value: value || '',
            onChange: (e) => onChange(e.target.value),
            onFocus: () => setShowSuggestions(true),
            onBlur: handleBlur,
            placeholder,
            className: 'rounded-none'
        }),
        showSuggestions && filteredSuggestions.length > 0 && el('div', {
            className: 'absolute z-50 top-full left-0 right-0 bg-white border border-stone-200 shadow-lg max-h-48 overflow-y-auto'
        },
            filteredSuggestions.map((suggestion, idx) =>
                el('div', {
                    key: idx,
                    className: 'px-3 py-2 hover:bg-stone-100 cursor-pointer flex justify-between items-center',
                    onMouseDown: (e) => {
                        e.preventDefault();
                        onChange(suggestion.name);
                        setShowSuggestions(false);
                    }
                },
                    el('span', null, suggestion.name),
                    suggestion.relationship && el('span', { className: 'text-xs text-stone-500' }, suggestion.relationship)
                )
            )
        )
    );
}

export default PersonSelect;
